import { Phone, MessageCircle, Mail, Globe, MapPin } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { LocationSection } from "@/components/home/LocationSection";
import { Reveal } from "@/components/site/primitives";
import { ApplyButton } from "@/components/site/AdmissionBadge";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { telHref, waHref } from "@/lib/links";
import { useApply } from "@/context/ApplyContext";
import { IMAGES } from "@/data/content";
import { useSEO } from "@/hooks/useSite";

export default function Contact() {
  const { config } = useSiteConfig();
  const apply = useApply();
  useSEO({
    title: "Contact | IAUE School of Basic Studies, Port Harcourt",
    description: "Contact the School of Basic Studies, IAUE — call, WhatsApp or email an admissions officer. St. John's Campus, Aba Road, Port Harcourt.",
  });

  const cards = [
    { icon: Phone, label: "Call", value: config.phonePrimary, href: telHref(config.phonePrimary), tone: "#0A1E4E", tid: "contact-call" },
    { icon: MessageCircle, label: "WhatsApp", value: "Chat with us", href: waHref(config.whatsapp), tone: "#25D366", tid: "contact-whatsapp" },
    { icon: Mail, label: "Email", value: config.email, href: `mailto:${config.email}`, tone: "#C0202A", tid: "contact-email" },
  ];

  return (
    <>
      <PageHero overline="Contact" title="Talk to an Admissions Officer"
        subtitle="We're here to answer your questions and guide your next step." crumb="Contact" image={IMAGES.collaborating} />

      <section className="py-16 sm:py-24 bg-white">
        <div className="container-x">
          <div className="grid sm:grid-cols-3 gap-6">
            {cards.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.08}>
                <a href={c.href} target={c.label === "WhatsApp" ? "_blank" : undefined} rel="noopener noreferrer"
                  className="group block h-full rounded-sm border border-slate-200 bg-[#F7F5EF] p-8 transition-all hover:-translate-y-1 hover:border-[#D4AF37] hover:shadow-lg" data-testid={c.tid}>
                  <div className="flex h-14 w-14 items-center justify-center rounded-sm text-white" style={{ background: c.tone }}>
                    <c.icon size={24} />
                  </div>
                  <div className="mt-5 text-xs font-bold uppercase tracking-wide text-slate-500">{c.label}</div>
                  <div className="mt-1 font-serif text-xl font-bold text-[#0A1E4E]">{c.value}</div>
                </a>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 grid lg:grid-cols-2 gap-8">
            <div className="rounded-sm border border-slate-200 p-8">
              <h3 className="font-serif text-2xl font-bold text-[#0A1E4E]">Other Enquiry Numbers</h3>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(config.phones || []).map((p) => (
                  <a key={p} href={telHref(p)} className="flex items-center gap-3 rounded-sm border border-slate-200 px-4 py-3 hover:border-[#0A1E4E]" data-testid={`contact-phone-${p.replace(/\s/g,'')}`}>
                    <Phone size={16} className="text-[#D4AF37]" /> <span className="font-medium text-slate-800">{p}</span>
                  </a>
                ))}
              </div>
              <div className="mt-6 space-y-3 text-slate-700">
                <a href={`https://${config.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-[#0A1E4E]" data-testid="contact-website">
                  <Globe size={18} className="text-[#D4AF37]" /> {config.website}
                </a>
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="mt-0.5 text-[#D4AF37]" />
                  <span>School of Basic Studies, St. John's Campus, Aba Road, Port Harcourt, Rivers State, Nigeria</span>
                </div>
              </div>
            </div>
            <div className="rounded-sm bg-[#0A1E4E] p-8 text-white flex flex-col justify-center">
              <h3 className="font-serif text-2xl font-bold">Send an Enquiry</h3>
              <p className="mt-2 text-blue-100/85">Submit your details and an admissions officer will reach out to guide your application.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button className="btn-gold" onClick={() => apply?.openApply()} data-testid="contact-open-form">Submit Enquiry</button>
                <ApplyButton className="btn-red" testId="contact-apply">Apply Now</ApplyButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LocationSection />
    </>
  );
}
